import type { ParsedUrlQuery } from "querystring";

export interface CodesmithExtensionContext {
  type: "linear_issue" | "github_actions_run";
  url: string;
  label: string;
  kind: string;
  hiddenContext: string;
}

export function parseCodesmithExtensionContext(
  query: ParsedUrlQuery,
): CodesmithExtensionContext | null {
  if (typeof query.context_url !== "string") return null;

  let url: URL;
  try {
    url = new URL(query.context_url);
  } catch {
    return null;
  }

  if (query.context_type === "linear_issue") {
    return parseLinearIssueContext(url);
  }
  if (query.context_type === "github_actions_run") {
    return parseGithubActionsRunContext(url);
  }
  return null;
}

function parseLinearIssueContext(url: URL): CodesmithExtensionContext | null {
  if (url.protocol !== "https:" || url.hostname !== "linear.app") return null;
  const match = url.pathname.match(
    /^\/[^/]+\/issue\/([A-Za-z][A-Za-z0-9]*-\d+)(?:\/|$)/,
  );
  if (!match) return null;

  const label = match[1].toUpperCase();
  return {
    type: "linear_issue",
    url: url.href,
    label,
    kind: "Linear issue",
    hiddenContext: `Read this Linear issue and use it as context: ${url.href}`,
  };
}

function parseGithubActionsRunContext(
  url: URL,
): CodesmithExtensionContext | null {
  if (url.protocol !== "https:" || url.hostname !== "github.com") return null;
  const match = url.pathname.match(
    /^\/([^/]+)\/([^/]+)\/actions\/runs\/(\d+)(?:\/attempts\/\d+)?(?:\/job\/(\d+))?(?:\/|$)/,
  );
  if (!match) return null;

  const runId = Number.parseInt(match[3], 10);
  if (!Number.isSafeInteger(runId) || runId < 1) return null;

  let jobId: number | null = null;
  if (match[4] !== undefined) {
    jobId = Number.parseInt(match[4], 10);
    if (!Number.isSafeInteger(jobId) || jobId < 1) return null;
  }

  url.hash = "";
  const href = url.href;
  const repository = match[2];
  const noun = jobId === null ? "run" : "job";

  return {
    type: "github_actions_run",
    url: href,
    label: `${repository} ${noun} ${jobId ?? runId}`,
    kind: `GitHub Actions ${noun}`,
    hiddenContext: `Use this GitHub Actions ${noun} as context: ${href}`,
  };
}

/** Carries validated context across a redirect; unknown context is dropped. */
export function applyCodesmithExtensionContext(
  params: URLSearchParams,
  query: ParsedUrlQuery,
): void {
  const context = parseCodesmithExtensionContext(query);
  if (!context) return;
  params.set("context_type", context.type);
  params.set("context_url", context.url);
}

export function codesmithExtensionSessionPath(
  org: string,
  query: ParsedUrlQuery,
): string {
  const params = new URLSearchParams({ embed: "extension" });
  applyCodesmithExtensionContext(params, query);
  if (typeof query.ref === "string") params.set("ref", query.ref);
  return `/${encodeURIComponent(org)}/sessions/new?${params.toString()}`;
}

export function codesmithExtensionExistingSessionPath(
  org: string,
  sessionId: string,
  query: ParsedUrlQuery,
): string {
  const params = new URLSearchParams({ embed: "extension" });
  if (typeof query.ref === "string") params.set("ref", query.ref);
  return `/${encodeURIComponent(org)}/sessions/${encodeURIComponent(
    sessionId,
  )}?${params.toString()}`;
}

export function withCodesmithExtensionContext(
  existing: string | undefined,
  context: CodesmithExtensionContext | null | undefined,
): string | undefined {
  return (
    [existing, context?.hiddenContext].filter(Boolean).join("\n\n") || undefined
  );
}
